/**
 * CC-14: the one place money and dates get formatted. Every future screen
 * showing a price, a scheduled time, or an invoice date must go through
 * these, not `` `$${amount}` `` or `date.toLocaleDateString()` inline — the
 * locale argument is what makes "$1,234.56" vs "1.234,56 €" a config change
 * later instead of a find-and-replace across the app.
 *
 * Defaults to the browser's own locale (`navigator.language`), not a
 * hardcoded `'en-US'` — money and dates should already read correctly for
 * whoever is looking at the screen, even before a second UI language ships.
 */

export function formatCurrency(
  amount: number,
  currency = 'USD',
  locale: string = navigator.language,
): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

export function formatDate(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
  locale: string = navigator.language,
): string {
  const value = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, options).format(value);
}

/**
 * A bare `DateOnly` string ("2026-10-27"), formatted as the calendar day it
 * names. ⚠️ On the UTC clock, and not by preference: `new Date("2026-10-27")`
 * is UTC midnight, so formatting it in the viewer's zone prints the PREVIOUS
 * day for anyone west of UTC — every US shop (M1-27, M1-37, M1-24 each met
 * this; `formatVehicleDate` now delegates here).
 */
export function formatDateOnly(value: string, locale: string = navigator.language): string {
  return formatDate(value, { dateStyle: 'medium', timeZone: 'UTC' }, locale);
}

export function formatDateTime(date: Date | string, locale: string = navigator.language): string {
  return formatDate(date, { dateStyle: 'medium', timeStyle: 'short' }, locale);
}

/**
 * "Aug 31, 2026, 9:05 AM GMT+14": an instant's date AND time from one
 * formatter, with the zone named, so the time reads as evidence (M2-08's
 * signed answer). Explicit parts, because `timeStyle` refuses `timeZoneName`.
 * `timeZone` omitted is the VIEWER's clock — the customer's page; the office
 * passes the shop's.
 */
export function formatInstantWithZone(
  date: Date | string,
  timeZone?: string,
  locale: string = navigator.language,
): string {
  return formatDate(
    date,
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      ...(timeZone ? { timeZone } : {}),
    },
    locale,
  );
}

/**
 * A time of day, read on a named clock rather than the viewer's (M1-09).
 *
 * The dispatch board prints the time a technician will actually knock on the
 * door, which is a time on the *business's* clock. Everywhere else in the app
 * the browser's own zone is the right answer and `timeZone` can be omitted;
 * here it is the whole point, so it is a parameter rather than a default.
 */
export function formatTime(
  date: Date | string,
  timeZone?: string,
  locale: string = navigator.language,
): string {
  return formatDate(date, { timeStyle: 'short', timeZone }, locale);
}

/**
 * "8:00 AM MDT": a time of day on a named clock, with the zone named — for a
 * screen whose times are not on the viewer's clock or the shop's but on the
 * appointment's (M2-21: the property's, else the shop's). Explicit parts, as
 * `timeStyle` refuses `timeZoneName`.
 */
export function formatTimeWithZone(
  date: Date | string,
  timeZone: string,
  locale: string = navigator.language,
): string {
  return formatDate(date, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone }, locale);
}

/** "MDT", "GMT+1": the short name a clock goes by at an instant. */
export function zoneName(date: Date | string, timeZone: string, locale: string = navigator.language): string {
  const value = typeof date === 'string' ? new Date(date) : date;
  return (
    new Intl.DateTimeFormat(locale, { timeZoneName: 'short', timeZone })
      .formatToParts(value)
      .find((part) => part.type === 'timeZoneName')?.value ?? timeZone
  );
}

/**
 * `zone` if this runtime knows it, else `fallback`. A zone id the API hands
 * over "as stored, whether or not it resolves" (M2-21's
 * `appointmentTimezone`) would otherwise make every `Intl` call on the page
 * throw a RangeError — a blank job page over one bad property row.
 */
export function usableTimeZone(zone: string | null | undefined, fallback: string): string {
  if (!zone) return fallback;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return fallback;
  }
}

/** A weekday and date, on a named clock — the dispatch board's day heading. */
export function formatBoardDate(
  date: Date | string,
  timeZone?: string,
  locale: string = navigator.language,
): string {
  return formatDate(
    date,
    { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone },
    locale,
  );
}
